import { usePerformanceTier } from './usePerformanceTier';

import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { groupIncludesEvaluationType } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';

export interface ManagerAssessmentAccess {
    /**
     * Whether the cycle's steps show what belongs to Manager Assessment: its tile, its launch
     * milestone, the Manager deadline and Discussion email milestones and their communications.
     */
    isShown: boolean;
    /**
     * Whether those Manager Assessment parts can only be read: shown as they are, never changed,
     * and left out of what the step validates and writes. The 360 and Self parts stay editable.
     */
    isReadOnly: boolean;
}

/**
 * What a feedback cycle's steps do with Manager Assessment.
 *
 * Performance PRO companies get it in full. A cycle that already includes one keeps showing it
 * after a downgrade, read-only: nothing a company made disappears or changes when it loses PRO.
 * Any other free-tier cycle leaves Manager Assessment out (its tile is offered locked instead).
 */
export const useManagerAssessmentAccess = (
    group: CycleEvaluationGroup | undefined,
): ManagerAssessmentAccess => {
    const { isPro } = usePerformanceTier();
    const includesManagerAssessment = groupIncludesEvaluationType(
        group,
        EvaluationType.MANAGER_EVALUATION,
    );

    return {
        isShown: isPro || includesManagerAssessment,
        isReadOnly: !isPro && includesManagerAssessment,
    };
};
